import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { SelectWorkflowComponent } from './common/selectcomponent'

class ConcaveHull extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.concavehull',
        group: 'Spatial Operations',
        title: 'ST Concave Hull',
        description: `
      This component creates a new table containing a single row with the concave hull of geographies in the input table.
      `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'maxedge',
          type: WorkflowComponentParamType.Number,
          title: 'Max edge (km)',
          default: 100,
          providers: [Provider.BigQuery, Provider.Snowflake]
        },
        {
          name: 'edgelengthratio',
          type: WorkflowComponentParamType.Number,
          title: 'Edge length ratio',
          min: 0,
          max: 1,
          default: 0.5,
          providers: [Provider.DatabricksRest]
        }
      ]
    )
    this.supportedProviders = [
      Provider.BigQuery,
      Provider.Snowflake,
      Provider.DatabricksRest
    ]
  }

  getInnerSql({ params, connection, envVars }: WorkflowComponentCodeParams) {
    switch (connection.type) {
      case Provider.BigQuery:
        return dedent`SELECT
          ${connection.quoteName(
            envVars.analyticsToolboxDataset as string
          )}.ST_CONCAVEHULL(
          ARRAY_AGG(${params.geo}),
          ${params.maxedge ?? 'NULL'},
          NULL
          ) geom
        FROM ${connection.quoteName(params.source as string)}`
      case Provider.Snowflake:
        return dedent`SELECT
          ${connection.quoteName(
            envVars.analyticsToolboxDataset as string
          )}.ST_CONCAVEHULL(
          ARRAY_AGG(ST_ASGEOJSON(${params.geo})::STRING)
          ${params.maxedge ? ',' + params.maxedge : ''}
          ) geom
        FROM ${connection.quoteName(params.source as string)}`
      case Provider.DatabricksRest:
        return dedent`SELECT /*+ REPARTITION(40, geom) */
          SEDONA_ST_ASBINARY(SEDONA_ST_CONCAVEHULL(
            SEDONA_ST_COLLECT(
              SEDONA_ST_GEOMFROMWKB(
                ${connection.processOutputGeogColumn(
                  `ST_UNION_AGG(${connection.processInputGeogColumn(
                    params.geo as string
                  )})`
                )}
              )
            ),
            ${params.edgelengthratio}
          ))  geom
        FROM ${connection.quoteName(params.source as string)}`
      default:
        return null
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    return '1.0.0'
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ connection }: GetOutputSchemaParams): TableSchema {
    return [
      {
        name: 'geom',
        type: connection.getGeoDataType()
      }
    ]
  }
}

export { ConcaveHull }
